import React, { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../stores';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2,
  Loader2, Save, Calendar as CalendarIcon, Wand2, Link2, XCircle, Badge
} from 'lucide-react';

// --- IMPORTS CALENDRIER ---
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "../lib/utils";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

// --- DATE PICKER RÉUTILISABLE ---
const DatePicker = ({ date, setDate, placeholder = "Choisir une date" }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "d MMMM yyyy", { locale: fr }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={setDate} initialFocus locale={fr} />
      </PopoverContent>
    </Popover>
  );
};

const ENTITIES = [
  { value: 'clients', label: 'Clients' },
  { value: 'leads', label: 'Prospects (Leads)' },
  { value: 'tours', label: 'Tournées' },
  { value: 'products', label: 'Produits' }
];

// Dictionnaire exact des champs attendus par l'API
const ENTITY_FIELDS = {
  clients: [
    { name: 'name', label: 'Nom Complet', required: true },
    { name: 'email', label: 'Email', required: false },
    { name: 'phone', label: 'Téléphone', required: false },
    { name: 'company', label: 'Entreprise', required: false },
    { name: 'address', label: 'Adresse postale', required: false },
    { name: 'notes', label: 'Notes', required: false }
  ],
  products: [
    { name: 'name', label: 'Nom du Produit', required: true },
    { name: 'price_ht', label: 'Prix HT', required: true },
    { name: 'category', label: 'Catégorie', required: true },
    { name: 'type', label: 'Type (produit/service)', required: false },
    { name: 'stock_quantity', label: 'Stock', required: false },
    { name: 'description', label: 'Description', required: false }
  ],
  leads: [
    { name: 'name', label: 'Nom du Prospect', required: true },
    { name: 'company', label: 'Entreprise', required: false },
    { name: 'email', label: 'Email', required: false },
    { name: 'phone', label: 'Téléphone', required: false },
    { name: 'source', label: 'Source', required: false },
    { name: 'status', label: 'Statut', required: false },
    { name: 'notes', label: 'Notes', required: false }
  ],
  tours: [
    { name: 'client_name', label: 'Nom du Client', required: true },
    { name: 'delivery_date', label: 'Date de livraison', required: true },
    { name: 'address', label: 'Adresse de livraison', required: true },
    { name: 'notes', label: 'Notes', required: false }
  ]
};

const STATUS_OPTIONS = {
  leads: [{ value: 'all', label: 'Tous' }, { value: 'new', label: 'Nouveaux' }, { value: 'won', label: 'Gagnés' }],
  tours: [{ value: 'all', label: 'Tous' }, { value: 'planned', label: 'Planifiées' }, { value: 'completed', label: 'Terminées' }],
  clients: [{ value: 'all', label: 'Tous' }],
  products: [{ value: 'all', label: 'Tous' }]
};

// ============================================================================
// COMPOSANT: MAPPAGE INTELLIGENT PERSONNALISÉ
// ============================================================================
const SmartMapperUI = ({ expectedFields, previewData, onCancel, onConfirm }) => {
  const incomingColumns = previewData?.columns || [];
  const sampleRow = previewData?.data?.[0] || {};

  // État du mapping: { "nom_champ_attendu": "nom_colonne_fichier" }
  const [mapping, setMapping] = useState({});

  // Auto-détection basique à l'initialisation
  useEffect(() => {
    const initialMapping = {};
    expectedFields.forEach(field => {
      // Cherche une colonne dont le nom ressemble au label ou au name du champ
      const match = incomingColumns.find(col =>
        col.toLowerCase().includes(field.name.toLowerCase()) ||
        col.toLowerCase().includes(field.label.toLowerCase())
      );
      if (match) initialMapping[field.name] = match;
    });
    setMapping(initialMapping);
  }, [expectedFields, incomingColumns]);

  // Validation: Tous les champs requis sont-ils mappés ?
  const isMappingValid = useMemo(() => {
    return expectedFields.every(field => !field.required || mapping[field.name]);
  }, [mapping, expectedFields]);

  const handleConfirm = () => {
    // On transforme les données brutes selon le mapping choisi
    const transformedData = previewData.data.map(row => {
      const newRow = {};
      expectedFields.forEach(field => {
        const sourceColumn = mapping[field.name];
        newRow[field.name] = sourceColumn ? row[sourceColumn] : null;
      });
      return newRow;
    });
    onConfirm(transformedData);
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
      <div className="bg-slate-50 dark:bg-slate-900 border rounded-xl overflow-hidden">
        {/* Header du tableau de mapping */}
        <div className="grid grid-cols-12 gap-4 p-4 border-b bg-slate-100 dark:bg-slate-800 font-semibold text-sm text-slate-600 dark:text-slate-300">
          <div className="col-span-5">Champs attendus (EventPro)</div>
          <div className="col-span-2 flex justify-center"><Link2 className="w-4 h-4" /></div>
          <div className="col-span-5">Vos colonnes importées</div>
        </div>

        {/* Lignes de mapping */}
        <div className="divide-y">
          {expectedFields.map(field => {
            const isMapped = !!mapping[field.name];
            const hasError = field.required && !isMapped;

            return (
              <div key={field.name} className={`grid grid-cols-12 gap-4 p-4 items-center transition-colors ${hasError ? 'bg-red-50/50 dark:bg-red-950/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>

                {/* Colonne Gauche : Champ Attendu */}
                <div className="col-span-5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{field.label}</span>
                    {field.required && <Badge variant="destructive" className="text-[10px] h-5 px-1.5">Requis</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono mt-1">{field.name}</p>
                </div>

                {/* Milieu : Visuel de connexion */}
                <div className="col-span-2 flex justify-center text-slate-300 dark:text-slate-600">
                  {isMapped ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5" />}
                </div>

                {/* Colonne Droite : Sélection de la colonne entrante */}
                <div className="col-span-5 space-y-2">
                  <Select
                    value={mapping[field.name] || "none"}
                    onValueChange={(val) => setMapping(prev => ({ ...prev, [field.name]: val === "none" ? null : val }))}
                  >
                    <SelectTrigger className={hasError ? "border-red-300 ring-red-100" : ""}>
                      <SelectValue placeholder="Sélectionner une colonne..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-muted-foreground italic">-- Ignorer ce champ --</SelectItem>
                      {incomingColumns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Aperçu de la donnée tirée du fichier */}
                  {isMapped && (
                    <div className="text-xs bg-slate-100 dark:bg-slate-800 p-2 rounded border font-mono truncate text-slate-500">
                      Ex: {sampleRow[mapping[field.name]] || <span className="italic">Vide</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={handleConfirm} disabled={!isMappingValid}>
          <Save className="w-4 h-4 mr-2" /> Valider et Importer
        </Button>
      </div>
    </div>
  );
};


// ============================================================================
// PAGE PRINCIPALE
// ============================================================================
const DataManagementPage = () => {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(false);

  // États Import
  const [importType, setImportType] = useState('clients');
  const [importMode, setImportMode] = useState('append');
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null); // { columns: [], data: [], total: 0 }
  const [password, setPassword] = useState('');
  const [activeWorkflow, setActiveWorkflow] = useState(null); // 'classic' ou 'smart'

  // États Export
  const [exportType, setExportType] = useState('leads');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [dateRange, setDateRange] = useState({ start: undefined, end: undefined });
  const [exportStatus, setExportStatus] = useState('all');

  const downloadTemplate = async () => { window.open(`${API_URL}/data/template/${importType}`, '_blank'); };

  // Gestion de l'upload du fichier (Sert pour le mode Classic et Smart)
  const handleFileUpload = async (e, workflowType) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setActiveWorkflow(workflowType);
    setLoading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const res = await fetch(`${API_URL}/data/import/preview/${importType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.columns) { data.columns = data.columns.map(c => c.replace('*', '').trim()); }
      setPreviewData(data);
    } catch (err) {
      toast.error("Erreur lecture fichier");
      setActiveWorkflow(null);
    } finally {
      setLoading(false);
      e.target.value = null; // Reset input
    }
  };

  // Validation finale vers le backend
  const commitImport = async (finalData) => {
    if (importMode === 'replace' && !password) { toast.error("Mot de passe requis"); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/data/import/commit/${importType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data: finalData, mode: importMode, password: password })
      });
      const result = await res.json();
      if (result.status === 'success') {
        toast.success(`${result.imported} éléments importés avec succès !`);
        resetImportState();
      } else {
        toast.error("Erreur lors de l'import");
      }
    } catch (err) { toast.error("Erreur serveur"); } finally { setLoading(false); }
  };

  const resetImportState = () => {
    setPreviewData(null);
    setFile(null);
    setPassword('');
    setActiveWorkflow(null);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      // MODIFICATION : Conversion des objets Date en string YYYY-MM-DD pour le backend
      const formatDate = (d) => d ? format(d, 'yyyy-MM-dd') : null;

      const filters = {
        format: exportFormat,
        status: exportStatus,
        start_date: formatDate(dateRange.start),
        end_date: formatDate(dateRange.end)
      };

      const response = await fetch(`${API_URL}/data/export/${exportType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(filters)
      });

      if (!response.ok) throw new Error("Erreur export");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `export_${exportType}.xlsx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
      }
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Export téléchargé !");

    } catch (e) {
      console.error(e);
      toast.error("Impossible de générer l'export");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-heading">Gestion des Données</h1>
        <p className="text-muted-foreground">Importez ou exportez vos bases de données en masse.</p>
      </div>

      <Tabs defaultValue="import" className="space-y-4">
        <TabsList>
          <TabsTrigger value="import" className="gap-2" onClick={resetImportState}><Upload className="w-4 h-4" /> Import (Excel/CSV)</TabsTrigger>
          <TabsTrigger value="export" className="gap-2"><Download className="w-4 h-4" /> Export</TabsTrigger>
        </TabsList>

        {/* --- ONGLET IMPORT --- */}
        <TabsContent value="import">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Importer des données</span>
                {activeWorkflow && (
                  <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                    Mode {activeWorkflow === 'smart' ? 'Intelligent' : 'Classique'}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">

              {/* Étape 1 : Paramètres généraux (Toujours visibles) */}
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${activeWorkflow ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="space-y-2">
                  <Label>Type de donnée</Label>
                  <Select value={importType} onValueChange={setImportType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ENTITIES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Mode d'import</Label>
                  <Select value={importMode} onValueChange={setImportMode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="append">Ajouter (Conserver doublons)</SelectItem>
                      <SelectItem value="update">Mettre à jour (Basé sur Email/Ref)</SelectItem>
                      <SelectItem value="replace" className="text-red-600 font-bold">Remplacer tout (Danger)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-1">
                  <Button variant="outline" onClick={downloadTemplate} className="w-full border-dashed">
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" /> Télécharger modèle strict
                  </Button>
                </div>
              </div>

              {/* Étape 2 : Choix du flux de travail (Si aucun fichier uploadé) */}
              {!activeWorkflow && (
                <div className="grid md:grid-cols-2 gap-4 mt-8">
                  {/* Mode Classique */}
                  <div className="border border-slate-200 rounded-xl p-6 text-center hover:shadow-md transition-all flex flex-col items-center justify-center relative overflow-hidden bg-white dark:bg-slate-900">
                    <Input
                      type="file" accept=".xlsx, .csv"
                      onChange={(e) => handleFileUpload(e, 'classic')}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={loading}
                    />
                    {loading ? <Loader2 className="w-10 h-10 animate-spin text-primary mb-3" /> : <FileSpreadsheet className="w-10 h-10 text-slate-400 mb-3" />}
                    <h3 className="font-semibold text-lg">J'ai utilisé le modèle strict</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xs">Mon fichier respecte exactement les colonnes du modèle téléchargé ci-dessus.</p>
                  </div>

                  {/* Mode Intelligent */}
                  <div className="border-2 border-primary/30 rounded-xl p-6 text-center hover:shadow-md transition-all flex flex-col items-center justify-center relative overflow-hidden bg-primary/5 cursor-pointer">
                    <Input
                      type="file" accept=".xlsx, .csv"
                      onChange={(e) => handleFileUpload(e, 'smart')}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={loading}
                    />
                    {loading ? <Loader2 className="w-10 h-10 animate-spin text-primary mb-3" /> : <Wand2 className="w-10 h-10 text-primary mb-3" />}
                    <h3 className="font-semibold text-lg text-primary">J'ai mon propre fichier</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-xs">Je veux relier moi-même mes colonnes aux champs du système.</p>
                  </div>
                </div>
              )}

              {/* Étape 3A : Vue Mappage Intelligent */}
              {activeWorkflow === 'smart' && previewData && (
                <div className="pt-4 border-t">
                  <SmartMapperUI
                    expectedFields={ENTITY_FIELDS[importType]}
                    previewData={previewData}
                    onCancel={resetImportState}
                    onConfirm={(mappedData) => commitImport(mappedData)}
                  />
                </div>
              )}

              {/* Étape 3B : Vue Prévisualisation Classique */}
              {activeWorkflow === 'classic' && previewData && (
                <div className="space-y-4 pt-4 border-t animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      Fichier lu ({previewData.total} lignes)
                    </h3>
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={resetImportState}>Annuler</Button>
                      <Button onClick={() => commitImport(previewData.data)} disabled={loading}>
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <Save className="w-4 h-4 mr-2" /> Lancer l'import
                      </Button>
                    </div>
                  </div>
                  {/* Reste du tableau classique de preview (Code original simplifié pour clarté) */}
                  <div className="border rounded-md overflow-hidden max-h-[300px] overflow-y-auto bg-card">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {previewData.columns.slice(0, 6).map(col => <TableHead key={col}>{col}</TableHead>)}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.data.slice(0, 5).map((row, i) => (
                          <TableRow key={i}>
                            {previewData.columns.slice(0, 6).map(col => (
                              <TableCell key={col} className="text-xs truncate max-w-[150px]">{String(row[col] || '')}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-muted-foreground italic">* Aperçu des 5 premières lignes et 6 premières colonnes.</p>
                </div>
              )}

            </CardContent>
          </Card>
        </TabsContent>

        {/* --- ONGLET EXPORT (inchangé par rapport à ta version) --- */}
        <TabsContent value="export">
          <Card>
            <CardHeader>
              <CardTitle>Exporter des données</CardTitle>
              <CardDescription>Téléchargez vos données pour analyse externe.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Colonne Gauche */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Données à exporter</Label>
                    <Select value={exportType} onValueChange={(v) => { setExportType(v); setExportStatus('all'); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ENTITIES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Format</Label>
                    <div className="flex gap-4">
                      <div
                        onClick={() => setExportFormat('xlsx')}
                        className={`cursor-pointer flex-1 border rounded-lg p-3 flex items-center gap-2 hover:bg-slate-50 ${exportFormat === 'xlsx' ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}
                      >
                        <FileSpreadsheet className="w-5 h-5 text-green-600" />
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Excel (.xlsx)</div>
                      </div>
                      <div
                        onClick={() => setExportFormat('csv')}
                        className={`cursor-pointer flex-1 border rounded-lg p-3 flex items-center gap-2 hover:bg-slate-50 ${exportFormat === 'csv' ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}
                      >
                        <div className="w-5 h-5 bg-slate-200 rounded flex items-center justify-center text-[10px] font-bold">CSV</div>
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">CSV (Brut)</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Colonne Droite : Filtres avec DATE PICKER */}
                <div className="space-y-4 border-l pl-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm">Filtres optionnels</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date de début</Label>
                      {/* UTILISATION DU NOUVEAU COMPOSANT */}
                      <DatePicker
                        date={dateRange.start}
                        setDate={(d) => setDateRange({ ...dateRange, start: d })}
                        placeholder="Début"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin</Label>
                      {/* UTILISATION DU NOUVEAU COMPOSANT */}
                      <DatePicker
                        date={dateRange.end}
                        setDate={(d) => setDateRange({ ...dateRange, end: d })}
                        placeholder="Fin"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Statut</Label>
                    <Select value={exportStatus} onValueChange={setExportStatus}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(STATUS_OPTIONS[exportType] || [{ value: 'all', label: 'Tous' }]).map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t flex justify-end">
                <Button size="lg" onClick={handleExport} disabled={loading} className="w-full md:w-auto">
                  {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
                  Générer l'export
                </Button>
              </div>

            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataManagementPage;